import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { pole } from '../utils';
import { inputStyle, labelStyle, btnPrimary, btnOutline, chyboveHlaseni, apiChyba } from '../ui';

const API = process.env.REACT_APP_API_URL || '/api';

// "Starostrašnická" -> "starostrasnicka" (hledání bez diakritiky a velikosti písmen)
const norm = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
const popisek = p => [p.nazev, p.adresa, p.mesto].filter(Boolean).join(' · ');

// Našeptávač prodejny zákazníka: hledá podle názvu, města i adresy. Chybí-li prodejna v seznamu,
// jde zadat novou (název/adresa povinné, město nepovinné) – založí se při uložení tiketu.
// Hodnota: { provozovnaId: string, nova: {nazev, mesto} | null }
export default function ProdejnaCombobox({ zakaznik, hodnota, onChange, chybne, vychoziText = '' }) {
  const prodejny = useMemo(() => (zakaznik?.provozovny || []).filter(p => p.aktivni !== false), [zakaznik]);
  const [text, setText] = useState(vychoziText);
  const [otevreno, setOtevreno] = useState(false);
  const [formNova, setFormNova] = useState(null);   // { nazev, mesto } rozepsaná nová prodejna
  const [podobne, setPodobne] = useState(null);     // nalezené podobné prodejny (varování před duplicitou)
  const [chyba, setChyba] = useState(null);
  const [kontroluji, setKontroluji] = useState(false);

  useEffect(() => { setText(vychoziText); }, [vychoziText]);
  useEffect(() => { setFormNova(null); setPodobne(null); setChyba(null); if (!hodnota.provozovnaId && !hodnota.nova) setText(''); },
    // eslint-disable-next-line
    [zakaznik?.id]);

  const nalezene = useMemo(() => {
    const h = norm(text);
    const vse = h ? prodejny.filter(p => norm([p.nazev, p.mesto, p.adresa].join(' ')).includes(h)) : prodejny;
    return vse.slice(0, 40);
  }, [prodejny, text]);

  const vyber = p => {
    onChange({ provozovnaId: String(p.id), nova: null });
    setText(popisek(p)); setOtevreno(false); setFormNova(null); setPodobne(null); setChyba(null);
  };
  const piš = v => { setText(v); setOtevreno(true); if (hodnota.provozovnaId || hodnota.nova) onChange({ provozovnaId: '', nova: null }); };

  const otevriFormNova = () => { setFormNova({ nazev: text.trim(), mesto: '' }); setPodobne(null); setChyba(null); setOtevreno(false); };

  const potvrdNovou = () => {
    onChange({ provozovnaId: '', nova: { nazev: formNova.nazev.trim(), mesto: formNova.mesto.trim() } });
    setText(''); setFormNova(null); setPodobne(null);
  };

  const zkontrolujANeboPotvrd = async () => {
    setChyba(null);
    if (!formNova.nazev.trim()) { setChyba('Zadejte název / adresu prodejny.'); return; }
    setKontroluji(true);
    try {
      const r = await axios.get(`${API}/zakaznici/${zakaznik.id}/provozovny/podobne`, { params: { nazev: formNova.nazev.trim(), mesto: formNova.mesto.trim() || undefined } });
      const nalez = pole(r.data);
      if (nalez.length) setPodobne(nalez); else potvrdNovou();
    } catch (e) { setChyba(apiChyba(e, 'Nepodařilo se zkontrolovat podobné prodejny.')); }
    setKontroluji(false);
  };

  const ramecek = chybne ? '2px solid #ef4444' : '1px solid #9ca3af';

  // vybraná nová prodejna
  if (hodnota.nova) {
    return (
      <div style={{ border: '2px solid #3ab54a', background: '#f0fdf4', borderRadius: '8px', padding: '10px 12px' }}>
        <strong>Nová prodejna:</strong> {hodnota.nova.nazev}{hodnota.nova.mesto ? `, ${hodnota.nova.mesto}` : ''}
        <div style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 8px' }}>Založí se v číselníku zákazníka při uložení tiketu a příště se nabídne v našeptávači.</div>
        <button type="button" style={{ ...btnOutline, minHeight: 0, padding: '6px 12px' }} onClick={() => { setFormNova({ ...hodnota.nova }); onChange({ provozovnaId: '', nova: null }); }}>Změnit</button>{' '}
        <button type="button" style={{ ...btnOutline, minHeight: 0, padding: '6px 12px' }} onClick={() => { onChange({ provozovnaId: '', nova: null }); setText(''); }}>Zrušit</button>
      </div>
    );
  }

  // rozepsaná nová prodejna
  if (formNova) {
    return (
      <div style={{ border: '2px solid #3ab54a', borderRadius: '8px', padding: '12px' }}>
        <strong>Nová prodejna</strong>
        <label style={labelStyle}>Název / adresa *</label>
        <input style={inputStyle} value={formNova.nazev} onChange={e => setFormNova({ ...formNova, nazev: e.target.value })} autoFocus />
        <label style={labelStyle}>Město (nepovinné)</label>
        <input style={inputStyle} value={formNova.mesto} onChange={e => setFormNova({ ...formNova, mesto: e.target.value })} />

        {podobne && (
          <div style={{ ...chyboveHlaseni, background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e' }}>
            <strong>Podobné prodejny už existují.</strong> Nejde o některou z nich?
            {podobne.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '6px 0', borderTop: '1px solid #fde68a', marginTop: '6px' }}>
                <span>{popisek(p)}</span>
                <button type="button" style={{ ...btnPrimary, minHeight: 0, padding: '6px 12px', fontSize: '14px' }} onClick={() => vyber(p)}>Použít tuto</button>
              </div>
            ))}
            <button type="button" style={{ ...btnOutline, marginTop: '10px', width: '100%' }} onClick={potvrdNovou}>Přesto založit novou prodejnu</button>
          </div>
        )}
        {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
        {!podobne && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button type="button" style={{ ...btnPrimary, flex: 1 }} onClick={zkontrolujANeboPotvrd} disabled={kontroluji}>{kontroluji ? 'Kontroluji…' : 'Použít novou prodejnu'}</button>
            <button type="button" style={btnOutline} onClick={() => setFormNova(null)}>Zpět</button>
          </div>
        )}
        {podobne && <button type="button" style={{ ...btnOutline, width: '100%', marginTop: '8px', border: 'none', color: '#6b7280' }} onClick={() => setFormNova(null)}>Zpět na hledání</button>}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={{ ...inputStyle, border: ramecek }} value={text} disabled={!zakaznik}
        placeholder={zakaznik ? 'Začněte psát název, město nebo adresu…' : 'Nejdřív vyberte zákazníka'}
        onChange={e => piš(e.target.value)} onFocus={() => setOtevreno(true)}
        onBlur={() => setTimeout(() => setOtevreno(false), 200)}
        onKeyDown={e => { if (e.key === 'Escape') setOtevreno(false); }}
        role="combobox" aria-expanded={otevreno} autoComplete="off"
      />
      {otevreno && zakaznik && (
        <div style={{ position: 'absolute', zIndex: 40, left: 0, right: 0, top: '100%', background: '#fff', border: '1px solid #9ca3af', borderRadius: '8px', maxHeight: '260px', overflowY: 'auto', boxShadow: '0 8px 20px rgba(0,0,0,0.15)' }}>
          {prodejny.length === 0 && <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '14px' }}>Zákazník zatím nemá žádné prodejny – zadejte novou.</div>}
          {nalezene.map(p => (
            <div key={p.id} role="option" onMouseDown={e => { e.preventDefault(); vyber(p); }}
              style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontWeight: 'bold' }}>{p.nazev}</div>
              {(p.adresa || p.mesto) && <div style={{ fontSize: '12px', color: '#6b7280' }}>{[p.adresa, p.mesto].filter(Boolean).join(', ')}</div>}
            </div>
          ))}
          {prodejny.length > 0 && nalezene.length === 0 && <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '14px' }}>Žádná prodejna neodpovídá.</div>}
          <div role="option" onMouseDown={e => { e.preventDefault(); otevriFormNova(); }}
            style={{ padding: '12px', cursor: 'pointer', color: '#15803d', fontWeight: 'bold', background: '#f0fdf4' }}>
            ➕ Přidat novou prodejnu{text.trim() ? ` „${text.trim()}“` : ''}
          </div>
        </div>
      )}
    </div>
  );
}
